import { useCallback, useMemo } from 'react';
import { ClinicUnits } from '../../teams/TeamListing/types';
import { omit } from 'lodash';

const mockTaskTemplates = [
  {
    clinic_id: 39,
    organisation_unit: 'team',
    clinic: 'Mental health',
    feature: {
      tasks: [
        {
          id: 'blood_pressure_check',
          task_name: 'Blood pressure check',
        },
        {
          id: 'contact_gp',
          task_name: 'Contact GP',
        },
      ],
    },
  },
  {
    clinic_id: 46,
    organisation_unit: 'team',
    clinic: 'RAP',
    feature: {
      tasks: [
        {
          id: 'order_blood_test',
          task_name: 'Order blood test',
        },
        {
          id: 'contact_community',
          task_name: 'Contact community',
        },
        {
          id: 'contact_gp',
          task_name: 'Contact GP',
        },
      ],
    },
  },
  {
    clinic_id: 12,
    clinic: 'Flinders hopsital',
    organisation_unit: 'hopsital',
    feature: {},
  },
];

const expetected = {
  blood_pressure_check: {
    clinicId_39: true,
    clinicId_46: false,
  },
  contact_gp: {
    clinicId_39: true,
    clinicId_46: true,
  },
  order_blood_test: {
    clinicId_39: false,
    clinicId_46: true,
  },
  contact_community: {
    clinicId_39: false,
    clinicId_46: true,
  },
};

function calculateTaskAvailability(clinics) {
  const taskAvailabilityResult = {};

  clinics
    .filter((clinic) => clinic.organisation_unit === ClinicUnits.TEAM) // Filter for 'team' only
    .forEach((clinic) => {
      const clinicIdKey = `clinicId_${clinic.clinic_id}`;
      const tasks = clinic?.features?.tasks || [];

      // Add each task in the clinic to the taskAvailabilityResult
      tasks.forEach((task) => {
        if (!taskAvailabilityResult[task.id]) {
          taskAvailabilityResult[task.id] = {
            taskName: task.task_name,
          };
        }
        taskAvailabilityResult[task.id][clinicIdKey] = true;
      });

      // Mark tasks not present in the current clinic as false
      Object.keys(taskAvailabilityResult).forEach((taskId) => {
        if (!(clinicIdKey in taskAvailabilityResult[taskId])) {
          taskAvailabilityResult[taskId][clinicIdKey] = false;
        }
      });
    });

  const columnData = [];

  Object.keys(taskAvailabilityResult).forEach((taskId) => {
    columnData.push({
      id: taskId,
      task: taskAvailabilityResult[taskId]['taskName'],
      teams: {
        ...omit(taskAvailabilityResult[taskId], 'taskName'),
      },
      isDeleted: false,
    });
  });

  return columnData;
}

function mapTeamIdAndName(clinics) {
  return clinics
    .filter((clinic) => clinic.organisation_unit === ClinicUnits.TEAM)
    .reduce((acc, curr) => {
      const clinicIdKey = `clinicId_${curr.clinic_id}`;
      acc[clinicIdKey] = curr.clinic;
      return acc;
    }, {});
}

function mapTeamIdAndDefaultTaskValues(clinics) {
  return clinics
    .filter((clinic) => clinic.organisation_unit === ClinicUnits.TEAM)
    .reduce((acc, curr) => {
      const clinicIdKey = `clinicId_${curr.clinic_id}`;
      acc[clinicIdKey] = false;
      return acc;
    }, {});
}

export const useTeamTaskTemplate = (clinics) => {
  const columnData = useMemo(
    () => calculateTaskAvailability(clinics),
    [clinics],
  );
  const teamsIdAndName = useMemo(() => mapTeamIdAndName(clinics), [clinics]);
  const teamIds = Object.keys(teamsIdAndName);

  const addNewTeam = useCallback(
    (newTaskName) => {
      const task = newTaskName;
      const taskId = task.replace(/ /g, '_').toLowerCase();
      return {
        task,
        id: taskId,
        teams: mapTeamIdAndDefaultTaskValues(clinics),
        isDeleted: false,
      };
    },
    [clinics],
  );

  const deleteTask = useCallback((taskId, currentValues) => {
    const toUpdateValues = currentValues.slice();
    const indexToUpdate = toUpdateValues.findIndex(
      (task) => task.id === taskId,
    );
    if (indexToUpdate > -1) {
      toUpdateValues[indexToUpdate].isDeleted = true;
    }

    return toUpdateValues;
  }, []);

  return {
    columnData,
    teamsIdAndName,
    addNewTeam,
    deleteTask,
    teamIds,
  };
};
